import { fontAttrs } from "@/components/tools/printables/page-kit";
import type { StoryboardGrid } from "@/lib/tool-kit/games-organizers/storyboard";
import { type PageGeometry, r2 } from "@/lib/tool-kit/printables/page";
import type { TextMeasure } from "@/lib/tool-kit/printables/text";

const INK = "#1a1a1a";
const MUTED = "#555555";
const RULE = "#8a8a8a";
const TITLE_MM = 7;
const MIN_TITLE_MM = 4;
const LABEL_MM = 3.4;
const BADGE_MM = 6;
const BOLD = { kind: "sans", bold: true } as const;
const SANS = { kind: "sans" } as const;

/** The title's font size: 7 mm, shrunk to fit the room beside the name line. */
function titleSize(text: string, room: number, measure: TextMeasure): number {
  const em = measure(text, BOLD);
  if (em <= 0) {
    return TITLE_MM;
  }
  return r2(Math.max(MIN_TITLE_MM, Math.min(TITLE_MM, room / em)));
}

/** One storyboard sheet: title strip, name and date, then the frames with caption rules. */
export default function StoryboardPage({
  geometry,
  grid,
  title,
  numbered,
  nameLine,
  nameLabel,
  dateLabel,
  measure,
}: {
  geometry: PageGeometry;
  grid: StoryboardGrid;
  title: string;
  numbered: boolean;
  nameLine: boolean;
  nameLabel: string;
  dateLabel: string;
  measure: TextMeasure;
}) {
  const right = geometry.left + geometry.contentWidth;
  const baseline = geometry.top + 7;
  const dateStart = right - 38;
  const nameStart = dateStart - 8 - 62;
  const room = nameLine ? nameStart - geometry.left - 4 : geometry.contentWidth;
  return (
    <g>
      {title ? (
        <text
          fill={INK}
          fontSize={titleSize(title, room, measure)}
          x={geometry.left}
          y={baseline}
          {...fontAttrs(BOLD)}
        >
          {title}
        </text>
      ) : null}
      {nameLine ? (
        <g>
          <text
            fill={MUTED}
            fontSize={LABEL_MM}
            x={nameStart}
            y={baseline}
            {...fontAttrs(SANS)}
          >
            {nameLabel}
          </text>
          <line
            stroke={INK}
            strokeWidth={0.25}
            x1={r2(nameStart + 12)}
            x2={r2(dateStart - 4)}
            y1={baseline}
            y2={baseline}
          />
          <text
            fill={MUTED}
            fontSize={LABEL_MM}
            x={dateStart}
            y={baseline}
            {...fontAttrs(SANS)}
          >
            {dateLabel}
          </text>
          <line
            stroke={INK}
            strokeWidth={0.25}
            x1={r2(dateStart + 10)}
            x2={right}
            y1={baseline}
            y2={baseline}
          />
        </g>
      ) : null}
      {grid.frames.map((frame, index) => (
        <g key={`${frame.x}-${frame.y}`}>
          <rect
            fill="#ffffff"
            height={grid.frameHeight}
            stroke={INK}
            strokeWidth={0.5}
            width={grid.frameWidth}
            x={frame.x}
            y={frame.y}
          />
          {numbered ? (
            <g>
              <rect
                fill={INK}
                height={BADGE_MM}
                width={BADGE_MM}
                x={frame.x}
                y={frame.y}
              />
              <text
                fill="#ffffff"
                fontSize={LABEL_MM + 0.6}
                textAnchor="middle"
                x={r2(frame.x + BADGE_MM / 2)}
                y={r2(frame.y + BADGE_MM * 0.7)}
                {...fontAttrs(BOLD)}
              >
                {index + 1}
              </text>
            </g>
          ) : null}
          {frame.rules.map((y) => (
            <line
              key={y}
              stroke={RULE}
              strokeWidth={0.25}
              x1={frame.x}
              x2={r2(frame.x + grid.frameWidth)}
              y1={y}
              y2={y}
            />
          ))}
        </g>
      ))}
    </g>
  );
}
